import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import { Divider } from './common/Catalyst/divider';
import { Button } from './common/Catalyst/button';
import { Dialog, DialogBody, DialogTitle } from './common/Catalyst/dialog';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
  ErrorMessage,
} from './common/Catalyst/fieldset';
import { Input } from './common/Catalyst/input';
import { hoaService, workOrderService } from './clients/ApiClients';
import {
  HoaReturn,
  WorkOrderReturn,
  WorkOrderUpdate,
} from './clients/rust-api-client';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from './common/Catalyst/dropdown';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { FieldPicker } from './common/Custom/FieldPicker';
import { Textarea } from './common/Catalyst/textarea';
import ShowAlert from './ShowAlert';
import { ErrorPage } from './common/Custom/ErrorPage';
import LoadingSpinner from './LoadingSpinner';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { canEditWorkOrder } from './constants/UserTypes';
import { apiErrorMessage } from './utils/apiErrors';
import { updateWorkOrder } from './sync/writes';
import { positionIn, readListContext } from './violation/linear/listContext';
import { noonUtcMillis, utcDay } from './violation/linear/violationMeta';
import { WorkOrderStatusIcon } from './workorder/linear/WorkOrderStatusIcon';
import { WorkOrderPropertiesPanel } from './workorder/linear/WorkOrderPropertiesPanel';
import { WorkOrderActivity } from './workorder/linear/WorkOrderActivity';
import { shortRef } from './workorder/linear/workOrderMeta';
import { fresher, useSyncedHoa, useSyncedWorkOrder } from './sync/hooks';
import { CreateActions } from './shell/CreateActions';
import {
  ChevronDownIcon as ChevronDownMini,
  ChevronUpIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/16/solid';

interface TouchedFields {
  [key: string]: boolean;
}

interface FormErrors {
  [key: string]: string;
}

// Helper to convert Unix timestamp or date string to YYYY-MM-DD for date inputs
const timestampToDateInput = (
  value: number | string | null | undefined
): string => {
  if (!value) return '';

  // If it's already a date string (YYYY-MM-DD), return it as-is
  if (typeof value === 'string') {
    // Validate it's a proper date string
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    // Try to parse other string formats
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    return '';
  }

  // It's a number (Unix timestamp in seconds)
  const date = new Date(value * 1000);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const typeOptions = [
  'maintenance',
  'repair',
  'improvement',
  'inspection',
  'landscaping',
  'cleaning',
  'other',
];

const priorityOptions = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
  { value: 'Urgent', label: 'Urgent' },
];

// Status values must match API format (PascalCase), labels are user-friendly
const statusOptions = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Scheduled', label: 'Scheduled' },
  { value: 'InProgress', label: 'In Progress' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const formatTypeLabel = (type: string) => {
  // Handle both PascalCase (InProgress) and snake_case (in_progress)
  return type
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const WorkOrderDetailPage: React.FC = () => {
  const { workOrderId } = useParams<{
    workOrderId: string;
  }>();
  const [loading, setLoading] = useState(true);
  const [ownWorkOrder, setWorkOrder] = useState<WorkOrderReturn | null>(null);
  // The sync engine's local copy when it has this work order (#561), or
  // what this page last got back from the server if that is newer.
  const syncedWorkOrder = useSyncedWorkOrder(workOrderId);
  const workOrder = fresher(syncedWorkOrder, ownWorkOrder);
  const haveSynced = !!syncedWorkOrder;
  const [showSaveError, setShowSaveError] = useState(false);
  const [saveErrorTitle, setSaveErrorTitle] = useState<string>('');
  const [saveError, setSaveError] = useState<string>('');

  const [showFetchError, setShowFetchError] = useState(false);
  const [fetchErrorTitle, setFetchErrorTitle] = useState<string>('');
  const [fetchError, setFetchError] = useState<string>('');
  // Bumped by the error page's "Try again" to run the load again.
  const [reloadKey, setReloadKey] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedWorkOrder, setEditedWorkOrder] =
    useState<WorkOrderUpdate | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touchedFields, setTouchedFields] = useState<TouchedFields>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const canEdit = canEditWorkOrder(getUserTypeFromToken());

  // In-place property edits (#603): one field at a time, saved on choose,
  // through the write queue (#561) — shown at once, undone if refused.
  const [savingField, setSavingField] = useState<string | null>(null);
  const [propertyError, setPropertyError] = useState<string | null>(null);

  // Previous / next within the list this work order was opened from.
  const navigate = useNavigate();
  const listContext = readListContext('workOrder');
  const position = workOrderId ? positionIn(listContext, workOrderId) : null;
  const backTo = listContext?.from ?? '/work-orders';

  useEffect(() => {
    const fetchWorkOrder = async () => {
      if (haveSynced) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await workOrderService.getWorkOrderById({ workOrderId: workOrderId! });
        setWorkOrder(response.data);
        setShowFetchError(false);
      } catch (err) {
        console.error('Error fetching work order:', err);
        const errorMessage = apiErrorMessage(
          err,
          "Couldn't load the work order."
        );

        setFetchErrorTitle("Couldn't load work order details");
        setFetchError(errorMessage);
        setShowFetchError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkOrder();
  }, [workOrderId, haveSynced, reloadKey]);

  // Its HOA's name, for the Association link.
  const hoaId = workOrder?.hoa_id;
  const [hoa, setHoa] = useState<HoaReturn | null>(null);
  const syncedHoa = useSyncedHoa(hoaId);
  const haveSyncedHoa = !!syncedHoa;
  useEffect(() => {
    if (!hoaId || haveSyncedHoa) return;
    let live = true;
    hoaService
      .getHoa({ id: hoaId })
      .then((r) => live && setHoa(r.data))
      // Not worth an alert: the link keeps showing the id.
      .catch((err) => console.error('Error fetching the work order\'s HOA:', err));
    return () => {
      live = false;
    };
  }, [hoaId, haveSyncedHoa]);
  const workOrderHoa = syncedHoa ?? (hoa?.id === hoaId ? hoa : null);

  const handlePropertyUpdate = async (patch: WorkOrderUpdate, field: string) => {
    if (!workOrderId) return;
    setSavingField(field);
    setPropertyError(null);
    try {
      // Through the write queue (#561): shown at once, taken back if
      // refused. Without a copy it is a plain request.
      const saved = await updateWorkOrder(workOrderId, patch);
      if (saved) setWorkOrder(saved);
    } catch (err) {
      console.error(`Error updating work order ${field}:`, err);
      setPropertyError(
        apiErrorMessage(err, 'The change was not saved. Please try again.')
      );
    } finally {
      setSavingField(null);
    }
  };

  // j / k step through the list, as in Linear — but never while the user
  // is typing, and never with a dialog open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isEditing || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) ||
          t.closest('[role="dialog"], [role="listbox"]'))
      )
        return;
      if (e.key === 'k' && position?.prevId) navigate(`/work-orders/${position.prevId}`);
      if (e.key === 'j' && position?.nextId) navigate(`/work-orders/${position.nextId}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isEditing, position?.prevId, position?.nextId, navigate]);

  const handleEdit = () => {
    if (workOrder) {
      const { id, date_created, date_updated, hoa_id, created_by, ...editableFields } =
        workOrder;
      setEditedWorkOrder(editableFields as WorkOrderUpdate);
      setIsEditing(true);
      setTouchedFields({});
      setFormErrors({});
    }
  };

  const handleSave = async () => {
    if (!editedWorkOrder || !workOrder) return;

    setIsSaving(true);
    setShowSaveError(false);
    setShowSuccessAlert(false);

    // Create a clean update payload
    const updatePayload: WorkOrderUpdate = {};

    // Only include fields that have changed
    if (editedWorkOrder.title !== workOrder.title) {
      updatePayload.title = editedWorkOrder.title;
    }

    if (editedWorkOrder.work_order_type !== workOrder.work_order_type) {
      updatePayload.work_order_type = editedWorkOrder.work_order_type;
    }

    if (editedWorkOrder.priority !== workOrder.priority) {
      updatePayload.priority = editedWorkOrder.priority;
    }

    if (editedWorkOrder.status !== workOrder.status) {
      updatePayload.status = editedWorkOrder.status;
    }

    if (editedWorkOrder.description !== workOrder.description) {
      updatePayload.description = editedWorkOrder.description;
    }

    if (editedWorkOrder.notes !== workOrder.notes) {
      updatePayload.notes = editedWorkOrder.notes;
    }

    if (editedWorkOrder.vendor_name !== workOrder.vendor_name) {
      updatePayload.vendor_name = editedWorkOrder.vendor_name;
    }

    if (editedWorkOrder.assigned_to !== workOrder.assigned_to) {
      updatePayload.assigned_to = editedWorkOrder.assigned_to;
    }

    if (editedWorkOrder.estimated_cost !== workOrder.estimated_cost) {
      updatePayload.estimated_cost = editedWorkOrder.estimated_cost;
    }

    if (editedWorkOrder.actual_cost !== workOrder.actual_cost) {
      updatePayload.actual_cost = editedWorkOrder.actual_cost;
    }

    // Dates: only when the day changed, and as epoch **milliseconds** at
    // noon UTC — what the API reads. (This used to send seconds on every
    // save, which the API read as a moment in January 1970.)
    const dateFields = ['scheduled_date', 'completion_date'] as const;
    dateFields.forEach((field) => {
      const day = timestampToDateInput(editedWorkOrder[field]);
      if (day && day !== utcDay(workOrder[field])) {
        const ms = noonUtcMillis(day);
        if (ms !== null) updatePayload[field] = ms;
      }
    });

    try {
      const response = await workOrderService.updateWorkOrder({
        workOrderId: workOrderId!,
        workOrderUpdate: updatePayload,
      });

      // Update local state
      setWorkOrder(response.data);

      setIsEditing(false);
      setSuccessMessage('Work order updated successfully!');
      setShowSuccessAlert(true);
    } catch (err) {
      console.error('Error updating work order:', err);
      const errorMessage = apiErrorMessage(
        err,
        "Couldn't save the work order."
      );

      setSaveErrorTitle("Couldn't update work order");
      setSaveError(errorMessage);
      setShowSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedWorkOrder(null);
    setFormErrors({});
    setTouchedFields({});
  };

  const handleInputChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | { target: { name: string; value: string } }
  ) => {
    if (!editedWorkOrder) return;
    const { name, value } = e.target;

    if (name === 'estimated_cost' || name === 'actual_cost') {
      // Remove any non-digit characters except decimal point
      const numericValue = value.replace(/[^\d.]/g, '');
      // Ensure only valid decimal numbers
      if (numericValue === '' || /^\d*\.?\d{0,2}$/.test(numericValue)) {
        setEditedWorkOrder((prev) => ({
          ...prev,
          [name]: numericValue === '' ? null : numericValue,
        }));
      }
    } else {
      setEditedWorkOrder((prev) => ({ ...prev, [name]: value }));
    }

    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof WorkOrderUpdate, value);
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof WorkOrderUpdate, value);
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  const isFormValid = () => {
    // Check for any existing errors
    if (Object.keys(formErrors).length > 0) {
      return false;
    }

    // Check required fields
    const requiredFields = ['title', 'work_order_type', 'description'];

    return requiredFields.every((field) => {
      const value = editedWorkOrder?.[field as keyof WorkOrderUpdate];
      if (!value) return false;
      return true;
    });
  };

  const isValidDate = (dateString: string | null | undefined): boolean => {
    if (!dateString) return true;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const validateField = (name: keyof WorkOrderUpdate, value: any): string => {
    switch (name) {
      case 'title':
        return !value
          ? 'Title is required'
          : value.length > 200
            ? 'Title must be less than 200 characters'
            : '';
      case 'work_order_type':
        return !typeOptions.includes(value)
          ? 'Please select a valid work order type'
          : '';
      case 'priority':
        return value && !priorityOptions.some((p) => p.value === value)
          ? 'Please select a valid priority'
          : '';
      case 'status':
        return value && !statusOptions.some((s) => s.value === value)
          ? 'Please select a valid status'
          : '';
      case 'description':
        return !value
          ? 'Description is required'
          : value.length > 1000
            ? 'Description must be less than 1000 characters'
            : '';
      case 'estimated_cost':
      case 'actual_cost':
        if (!value) return '';
        if (isNaN(value) || Number(value) < 0) {
          return 'Cost must be a positive number';
        }
        if (value > 1000000) {
          return 'Cost cannot exceed $1,000,000';
        }
        return '';
      case 'scheduled_date':
      case 'completion_date':
        return !isValidDate(value) ? 'Please enter a valid date' : '';
      default:
        return '';
    }
  };

  // With the row in the sync store there is nothing to wait for: render
  // it on the first paint, not after the effect clears `loading` (#635).
  if (loading && !haveSynced) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  // A failed load leaves nothing to show; say so rather than render an
  // empty page (#652).
  if (!workOrder) {
    return (
      <SidebarLayout
        sidebar={<AppSidebar currentPage={'work-orders'} />}
        navbar={<Navbar />}
      >
        <ErrorPage
          title="Couldn't load this work order"
          message={
            fetchError ||
            'It may have been deleted, or you may not have access to it.'
          }
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={'work-orders'} />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      {/* Header strip: where you came from, what this is, where you are
          in the list, and the overflow menu — as on a violation. */}
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to={backTo} className="shrink-0 whitespace-nowrap text-fg-muted hover:text-fg">
          Work Orders
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        {workOrder && (
          <>
            <WorkOrderStatusIcon status={workOrder.status} />
            <span className="hidden font-mono text-[12px] text-fg-muted sm:inline">
              {shortRef(workOrder.id)}
            </span>
            <span className="min-w-0 truncate font-medium text-fg">
              {workOrder.title}
            </span>
          </>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-1">
          {/* For the same association and, when it has one, the same home. */}
          {workOrder && (
            <span className="mr-2 flex items-center gap-2">
              <CreateActions
                scope={{ hoaId: workOrder.hoa_id, homeId: workOrder.home_id }}
                violation
                workOrder
              />
            </span>
          )}
          {position && (
            <>
              <span
                className="mr-1 text-[12px] tabular-nums text-fg-muted"
                aria-label={`Work order ${position.index + 1} of ${position.total}`}
              >
                {position.index + 1} / {position.total}
              </span>
              <button
                type="button"
                onClick={() => position.prevId && navigate(`/work-orders/${position.prevId}`)}
                disabled={!position.prevId}
                title="Previous (k)"
                aria-label="Previous work order"
                className="rounded p-1 text-fg-muted hover:bg-fg/5 hover:text-fg disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronUpIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => position.nextId && navigate(`/work-orders/${position.nextId}`)}
                disabled={!position.nextId}
                title="Next (j)"
                aria-label="Next work order"
                className="rounded p-1 text-fg-muted hover:bg-fg/5 hover:text-fg disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronDownMini className="h-4 w-4" />
              </button>
            </>
          )}
          {canEdit && workOrder && (
            <Dropdown>
              <DropdownButton plain aria-label="More actions">
                <EllipsisHorizontalIcon className="h-4 w-4" />
              </DropdownButton>
              <DropdownMenu anchor="bottom end">
                <DropdownItem onClick={handleEdit}>Edit all fields…</DropdownItem>
              </DropdownMenu>
            </Dropdown>
          )}
        </div>
      </div>

      <ShowAlert
        alertType="success"
        alertTitle="Success"
        alertMessage={successMessage}
        isVisible={showSuccessAlert}
        onClose={() => setShowSuccessAlert(false)}
        timeout={5000}
        isInline={true}
      />
      <ShowAlert
        alertType="error"
        alertTitle={fetchErrorTitle}
        alertMessage={fetchError}
        isVisible={showFetchError}
        onClose={() => setShowFetchError(false)}
        timeout={5000}
        isInline={true}
      />
      <ShowAlert
        alertType="error"
        alertTitle="Change not saved"
        alertMessage={propertyError ?? ''}
        isVisible={propertyError !== null}
        onClose={() => setPropertyError(null)}
        timeout={8000}
        isInline={true}
      />

      {workOrder && (
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="min-w-0 space-y-8">
            <div>
              <h1 className="text-2xl/8 font-semibold tracking-tight text-fg">
                {workOrder.title}
              </h1>
              <p className="mt-3 whitespace-pre-line text-[15px]/6 text-fg-secondary">
                {workOrder.description}
              </p>
            </div>

            {workOrder.notes && (
              <section aria-labelledby="work-order-notes">
                <h2
                  id="work-order-notes"
                  className="mb-2 text-[13px] font-medium text-fg-muted"
                >
                  Notes
                </h2>
                <p className="whitespace-pre-line rounded-lg border border-line px-4 py-3 text-[13px]/5 text-fg-secondary">
                  {workOrder.notes}
                </p>
              </section>
            )}

            <WorkOrderActivity workOrder={workOrder} />
          </div>

          {/* On narrow screens the panel drops below the main column. */}
          <aside
            aria-label="Work order properties"
            className="lg:sticky lg:top-6 lg:self-start"
          >
            <WorkOrderPropertiesPanel
              workOrder={workOrder}
              canEdit={canEdit}
              saving={savingField}
              onUpdate={handlePropertyUpdate}
              hoa={workOrderHoa}
            />
          </aside>
        </div>
      )}

      <Dialog
        open={isEditing}
        onClose={() => setIsEditing(false)}
        className="w-full max-w-2xl mx-auto"
      >
        <div className="relative flex flex-col h-full">
          <div className="p-4 md:p-6">
            <DialogTitle className="text-xl font-semibold">
              Edit Work Order
            </DialogTitle>
            <button
              onClick={() => setIsEditing(false)}
              className="absolute right-4 top-4 p-2 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-full transition-colors duration-150"
              aria-label="Close dialog"
            >
              <XMarkIcon className="h-5 w-5 text-zinc-600 dark:text-zinc-100" />
            </button>

            <Divider className="my-4" />

            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle={saveErrorTitle}
                alertMessage={saveError}
                isVisible={showSaveError}
                onClose={() => setShowSaveError(false)}
                timeout={5000}
                isInline={true}
              />
            </div>

            <DialogBody>
              <Fieldset>
                <FieldGroup>
                  {/* Title */}
                  <Field>
                    <Label htmlFor="title">Title*</Label>
                    <Input
                      id="title"
                      name="title"
                      type="text"
                      value={editedWorkOrder?.title || ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Enter work order title"
                      required
                      className="w-full"
                      data-invalid={touchedFields.title && formErrors.title}
                    />
                    {touchedFields.title && formErrors.title && (
                      <ErrorMessage>{formErrors.title}</ErrorMessage>
                    )}
                  </Field>

                  {/* Type, Priority, and Status */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field>
                      <Label>Type*</Label>
                      <FieldPicker
                        label="Type"
                        options={typeOptions.map((type) => ({
                          value: type,
                          label: formatTypeLabel(type),
                        }))}
                        value={editedWorkOrder?.work_order_type}
                        onChange={(value) =>
                          handleInputChange({
                            target: { name: 'work_order_type', value },
                          })
                        }
                        placeholder="Select Type"
                        invalid={
                          !!(touchedFields.work_order_type && formErrors.work_order_type)
                        }
                      />
                      {touchedFields.work_order_type &&
                        formErrors.work_order_type && (
                          <ErrorMessage>
                            {formErrors.work_order_type}
                          </ErrorMessage>
                        )}
                    </Field>

                    <Field>
                      <Label>Priority</Label>
                      <FieldPicker
                        label="Priority"
                        options={priorityOptions}
                        value={editedWorkOrder?.priority}
                        onChange={(value) =>
                          handleInputChange({
                            target: { name: 'priority', value },
                          })
                        }
                        placeholder="Select Priority"
                        invalid={!!(touchedFields.priority && formErrors.priority)}
                      />
                      {touchedFields.priority && formErrors.priority && (
                        <ErrorMessage>{formErrors.priority}</ErrorMessage>
                      )}
                    </Field>

                    <Field>
                      <Label>Status</Label>
                      <FieldPicker
                        label="Status"
                        options={statusOptions}
                        value={editedWorkOrder?.status}
                        onChange={(value) =>
                          handleInputChange({
                            target: { name: 'status', value },
                          })
                        }
                        placeholder="Select Status"
                        invalid={!!(touchedFields.status && formErrors.status)}
                      />
                      {touchedFields.status && formErrors.status && (
                        <ErrorMessage>{formErrors.status}</ErrorMessage>
                      )}
                    </Field>
                  </div>

                  {/* Scheduled Date and Completion Date */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field>
                      <Label>Scheduled Date</Label>
                      <Input
                        type="date"
                        name="scheduled_date"
                        value={timestampToDateInput(
                          editedWorkOrder?.scheduled_date
                        )}
                        onChange={handleInputChange}
                        className="w-full"
                      />
                    </Field>

                    <Field>
                      <Label>Completion Date</Label>
                      <Input
                        type="date"
                        name="completion_date"
                        value={timestampToDateInput(
                          editedWorkOrder?.completion_date
                        )}
                        onChange={handleInputChange}
                        className="w-full"
                      />
                    </Field>
                  </div>

                  {/* Estimated Cost and Actual Cost */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field>
                      <Label htmlFor="estimated_cost">Estimated Cost</Label>
                      <Input
                        id="estimated_cost"
                        name="estimated_cost"
                        type="number"
                        min="0"
                        step="0.01"
                        value={editedWorkOrder?.estimated_cost || ''}
                        onChange={handleInputChange}
                        placeholder="Enter estimated cost"
                        className="w-full"
                      />
                      {touchedFields.estimated_cost &&
                        formErrors.estimated_cost && (
                          <ErrorMessage>
                            {formErrors.estimated_cost}
                          </ErrorMessage>
                        )}
                    </Field>

                    <Field>
                      <Label htmlFor="actual_cost">Actual Cost</Label>
                      <Input
                        id="actual_cost"
                        name="actual_cost"
                        type="number"
                        min="0"
                        step="0.01"
                        value={editedWorkOrder?.actual_cost || ''}
                        onChange={handleInputChange}
                        placeholder="Enter actual cost"
                        className="w-full"
                      />
                      {touchedFields.actual_cost && formErrors.actual_cost && (
                        <ErrorMessage>{formErrors.actual_cost}</ErrorMessage>
                      )}
                    </Field>
                  </div>

                  {/* Vendor and Assigned To */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field>
                      <Label htmlFor="vendor_name">Vendor Name</Label>
                      <Input
                        id="vendor_name"
                        name="vendor_name"
                        type="text"
                        value={editedWorkOrder?.vendor_name || ''}
                        onChange={handleInputChange}
                        placeholder="Enter vendor name"
                        className="w-full"
                      />
                    </Field>

                    <Field>
                      <Label htmlFor="assigned_to">Assigned To</Label>
                      <Input
                        id="assigned_to"
                        name="assigned_to"
                        type="text"
                        value={editedWorkOrder?.assigned_to || ''}
                        onChange={handleInputChange}
                        placeholder="Enter assignee"
                        className="w-full"
                      />
                    </Field>
                  </div>

                  {/* Description */}
                  <Field>
                    <Label htmlFor="description">Description*</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={editedWorkOrder?.description ?? ''}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="Describe the work to be done"
                      required
                      className="w-full"
                      data-invalid={
                        touchedFields.description && formErrors.description
                      }
                      rows={3}
                      maxLength={1000}
                    />
                    {touchedFields.description && formErrors.description && (
                      <ErrorMessage>{formErrors.description}</ErrorMessage>
                    )}
                  </Field>

                  {/* Notes */}
                  <Field>
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={editedWorkOrder?.notes ?? ''}
                      onChange={handleInputChange}
                      placeholder="Additional notes"
                      className="w-full"
                      rows={2}
                    />
                  </Field>
                </FieldGroup>
              </Fieldset>
            </DialogBody>
          </div>

          <div className="sticky bottom-0 mt-auto border-t border-gray-200 dark:border-gray-700 bg-stone-100 dark:bg-gray-800 p-4 md:p-6">
            <div className="flex flex-col space-y-3">
              <Button
                onClick={handleSave}
                color="sky"
                disabled={isSaving || !isFormValid()}
                className="w-full disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                onClick={handleCancel}
                color="zinc"
                disabled={isSaving}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </SidebarLayout>
  );
};

export default WorkOrderDetailPage;
